import { useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { Folder } from '../types/models'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface UpdateFolderOrderRequest {
  folderId: string
  newOrder: number
}

export function useFolderOrder() {
  const { firebaseUser } = useAuth()
  const queryClient = useQueryClient()
  
  const updateFolderOrderMutation = useMutation({
    mutationFn: async (request: UpdateFolderOrderRequest) => {
      if (!firebaseUser) throw new Error('Not authenticated')
      
      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId
      
      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/folders/${request.folderId}/order`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          order: request.newOrder
        })
      })
      
      if (!response.ok) {
        throw new Error(`Failed to update folder order: ${response.statusText}`)
      }
      
      return response.json()
    },
    onSuccess: () => {
      // フォルダ一覧を再取得
      queryClient.invalidateQueries({ queryKey: ['folders'] })
    }
  })
  
  const reorderFolders = useCallback((folders: Folder[], dragIndex: number, hoverIndex: number) => {
    const draggedFolder = folders[dragIndex]
    const newFolders = [...folders]
    
    // 配列から要素を削除
    newFolders.splice(dragIndex, 1)
    // 新しい位置に挿入
    newFolders.splice(hoverIndex, 0, draggedFolder)
    
    return newFolders
  }, [])
  
  const updateFolderOrder = useCallback(async (folderId: string, newOrder: number) => {
    try {
      await updateFolderOrderMutation.mutateAsync({ folderId, newOrder })
    } catch (error) {
      console.error('Failed to update folder order:', error)
      throw error
    }
  }, [updateFolderOrderMutation])
  
  return {
    reorderFolders,
    updateFolderOrder,
    isUpdating: updateFolderOrderMutation.isPending
  }
}
